import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  Download, 
  CheckCircle, 
  AlertTriangle,
  Info,
  ExternalLink,
  RefreshCw
} from 'lucide-react';
import { usePWAInstall } from '../hooks/usePWAInstall';

const InstallButton = styled.button<{ $variant: 'primary' | 'secondary' | 'success' | 'warning' }>`
  background: ${({ $variant, theme }) => {
    switch ($variant) {
      case 'primary': return theme.colors.primary;
      case 'secondary': return theme.colors.backgroundSecondary;
      case 'success': return '#16a34a';
      case 'warning': return '#f59e0b';
      default: return theme.colors.primary;
    }
  }};
  color: ${({ $variant }) => 
    $variant === 'secondary' ? 'inherit' : 'white'
  };
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  min-width: 120px;
  
  &:hover {
    background: ${({ $variant, theme }) => {
      switch ($variant) {
        case 'primary': return theme.colors.primaryDark;
        case 'secondary': return theme.colors.gray200;
        case 'success': return '#15803d';
        case 'warning': return '#d97706';
        default: return theme.colors.primaryDark;
      }
    }};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusIndicator = styled.div<{ $status: 'installed' | 'available' | 'unavailable' | 'loading' }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  background: ${({ $status }) => {
    switch ($status) {
      case 'installed': return 'rgba(34, 197, 94, 0.1)';
      case 'available': return 'rgba(59, 130, 246, 0.1)';
      case 'unavailable': return 'rgba(107, 114, 128, 0.1)';
      case 'loading': return 'rgba(245, 158, 11, 0.1)';
      default: return 'rgba(107, 114, 128, 0.1)';
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
      case 'installed': return '#16a34a';
      case 'available': return '#2563eb';
      case 'unavailable': return '#6b7280';
      case 'loading': return '#f59e0b';
      default: return '#6b7280';
    }
  }};
  border: 1px solid ${({ $status }) => {
    switch ($status) {
      case 'installed': return 'rgba(34, 197, 94, 0.2)';
      case 'available': return 'rgba(59, 130, 246, 0.2)';
      case 'unavailable': return 'rgba(107, 114, 128, 0.2)';
      case 'loading': return 'rgba(245, 158, 11, 0.2)';
      default: return 'rgba(107, 114, 128, 0.2)';
    }
  }};
`;

const QuickGuide = styled.div`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  padding: 8px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 8px;
  line-height: 1.4;
`;

interface OnDemandInstallProps {
  variant?: 'primary' | 'secondary' | 'success' | 'warning';
  showStatus?: boolean;
  showQuickGuide?: boolean;
  className?: string;
  onInstall?: () => void;
  onError?: (error: Error) => void;
}

const OnDemandInstall: React.FC<OnDemandInstallProps> = ({
  variant = 'primary',
  showStatus = true,
  showQuickGuide = true,
  className,
  onInstall,
  onError
}) => {
  const {
    isInstalled,
    canInstall,
    isIOSDevice,
    isAndroidDevice,
    isIncognito,
    handleInstallClick
  } = usePWAInstall();
  
  const [isInstalling, setIsInstalling] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const getStatus = () => {
    if (isInstalling) return 'loading';
    if (isInstalled) return 'installed';
    if (canInstall && !isIncognito) return 'available';
    return 'unavailable';
  };

  const getStatusText = () => {
    const status = getStatus();
    switch (status) {
      case 'installed': return 'App Installed';
      case 'available': return 'Install Available';
      case 'loading': return 'Installing...';
      case 'unavailable': return 'Install Unavailable';
      default: return 'Unknown';
    }
  };

  const getStatusIcon = () => {
    const status = getStatus();
    switch (status) {
      case 'installed': return <CheckCircle size={12} />;
      case 'available': return <Download size={12} />;
      case 'loading': return <RefreshCw size={12} className="animate-spin" />;
      case 'unavailable': return <AlertTriangle size={12} />;
      default: return <Info size={12} />;
    }
  };

  const getButtonText = () => {
    if (isInstalling) return 'Installing...';
    if (isInstalled) return 'App Installed';
    if (canInstall && !isIncognito) return 'Install App';
    return 'Install App';
  };

  const getButtonIcon = () => {
    if (isInstalling) return <RefreshCw size={14} className="animate-spin" />;
    if (isInstalled) return <CheckCircle size={14} />;
    return <Download size={14} />;
  };

  const handleInstall = async () => {
    if (isInstalled) {
      onInstall?.();
      return;
    }

    if (!canInstall || isIncognito) {
      onError?.(new Error('Installation not available'));
      return;
    }

    setIsInstalling(true);
    setLastError(null);

    try {
      const success = await handleInstallClick();
      
      if (success) {
        onInstall?.();
      } else {
        onError?.(new Error('Installation failed'));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Installation failed';
      setLastError(errorMessage);
      onError?.(error instanceof Error ? error : new Error(errorMessage));
    } finally {
      setIsInstalling(false);
    }
  };

  const getQuickGuide = () => {
    if (isInstalled) return null;
    
    if (isIOSDevice) {
      return (
        <QuickGuide>
          <strong>iOS:</strong> Tap share button → "Add to Home Screen"
        </QuickGuide>
      );
    }
    
    if (isAndroidDevice) {
      return (
        <QuickGuide>
          <strong>Android:</strong> Tap menu → "Install app"
        </QuickGuide>
      );
    }
    
    return (
      <QuickGuide>
        <strong>Desktop:</strong> Look for install icon in address bar
      </QuickGuide>
    );
  };

  const getButtonVariant = () => {
    if (isInstalled) return 'success';
    if (isInstalling) return 'warning';
    if (canInstall && !isIncognito) return variant;
    return 'secondary';
  };

  return (
    <>
      <div className={className}>
        {showStatus && (
          <StatusIndicator $status={getStatus()}>
            {getStatusIcon()}
            {getStatusText()}
          </StatusIndicator>
        )}
        
        <InstallButton
          $variant={getButtonVariant()}
          onClick={handleInstall}
          disabled={isInstalling}
        >
          {getButtonIcon()}
          {getButtonText()}
        </InstallButton>
        
        {!isInstalled && showQuickGuide && getQuickGuide()}
        
        {lastError && (
          <div style={{ 
            color: '#dc2626', 
            fontSize: '12px', 
            marginTop: '4px',
            textAlign: 'center'
          }}>
            {lastError}
          </div>
        )}
        
        {!isInstalled && !canInstall && !isIncognito && (
          <div style={{ 
            marginTop: '8px',
            textAlign: 'center'
          }}>
            <button
              onClick={() => onError?.(new Error('Installation instructions not available'))}
              style={{
                background: 'none',
                border: 'none',
                color: '#2563eb',
                fontSize: '12px',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              <ExternalLink size={12} style={{ marginRight: '4px' }} />
              Show Instructions
            </button>
          </div>
        )}
      </div>
      
    </>
  );
};

export default OnDemandInstall;
